import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Input,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { FaDownload, FaCopy, FaClock, FaExclamationTriangle } from "react-icons/fa";
import { getSurveyQRCode } from "../../../helpers/surveyApi";
import { successMessage, dangerMessage } from "../../Toast/Toast";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { Label } from "reactstrap";

const SurveyQRCode = ({ surveyId, surveyTitle, token, onClose }) => {
  const [qrCode, setQrCode] = useState(null);
  const [surveyUrl, setSurveyUrl] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  
  // ✅ NEW: Countdown state
  const [availabilityEndDate, setAvailabilityEndDate] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isExpired: false,
  });

  useEffect(() => {
    fetchQRCode();
  }, [surveyId]);

  // ✅ NEW: Countdown timer effect
  useEffect(() => {
    if (!availabilityEndDate) return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const end = new Date(availabilityEndDate).getTime();
      const diff = end - now;

      if (diff <= 0) {
        setTimeRemaining({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          isExpired: true,
        });
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeRemaining({
        days,
        hours,
        minutes,
        seconds,
        isExpired: false,
      });
    };

    // Update immediately
    updateCountdown();

    // Update every second
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [availabilityEndDate]);

  const fetchQRCode = async () => {
    setIsLoading(true);
    try {
      const result = await getSurveyQRCode(surveyId, token);
      if (result?.status === "success") {
        setQrCode(result.data?.qrCode);
        setSurveyUrl(result.data?.surveyUrl || "");
        // ✅ NEW: Set availability end date
        if (result.data?.availabilityEndDate) {
          setAvailabilityEndDate(result.data.availabilityEndDate);
        }
      } else {
        dangerMessage(result?.message || "Failed to load QR code");
      }
    } catch (error) {
      dangerMessage("Error loading QR code");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = () => {
    if (!qrCode) return;

    try {
      const link = document.createElement("a");
      link.href = qrCode;
      link.download = `survey-${surveyTitle || "qr-code"}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      successMessage("QR code downloaded successfully");
    } catch (error) {
      dangerMessage("Failed to download QR code");
      console.error(error);
    }
  };

  const handleCopyLink = () => {
    successMessage("Link copied to clipboard!");
  };

  // ✅ NEW: Get countdown color based on time remaining
  const getCountdownColor = () => {
    if (timeRemaining.isExpired) return "#ef4444"; // Red
    if (timeRemaining.days > 1) return "#10b981"; // Green
    if (timeRemaining.days === 1 || timeRemaining.hours > 12) return "#f59e0b"; // Yellow/Orange
    return "#ef4444"; // Red (less than 12 hours)
  };

  // ✅ NEW: Get countdown status text
  const getCountdownStatus = () => {
    if (timeRemaining.isExpired) return "Survey Expired";
    if (timeRemaining.days > 1) return "Survey Available";
    if (timeRemaining.days === 1 || timeRemaining.hours > 12) return "Ending Soon";
    return "Ending Very Soon";
  };

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Survey QR Code</h3>
        <Button color="secondary" onClick={onClose}>
          Close
        </Button>
      </div>

      <Card>
        <CardHeader>
          <h5 className="mb-0">{surveyTitle}</h5>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center p-5">
              <Spinner color="primary" />
            </div>
          ) : qrCode ? (
            <div className="text-center">
              {/* ✅ NEW: Countdown Timer */}
              {availabilityEndDate && (
                <div
                  className="mb-4 p-3"
                  style={{
                    background: `${getCountdownColor()}15`,
                    border: `2px solid ${getCountdownColor()}`,
                    borderRadius: "12px",
                    maxWidth: "500px",
                    margin: "0 auto 24px",
                  }}
                >
                  <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                    <FaClock style={{ color: getCountdownColor() }} />
                    <span
                      style={{
                        color: getCountdownColor(),
                        fontWeight: "600",
                        fontSize: "14px",
                      }}
                    >
                      {getCountdownStatus()}
                    </span>
                  </div>

                  {timeRemaining.isExpired ? (
                    <div className="d-flex align-items-center justify-content-center gap-2">
                      <FaExclamationTriangle style={{ color: "#ef4444" }} />
                      <span style={{ color: "#ef4444", fontWeight: "600" }}>
                        Survey is no longer available
                      </span>
                    </div>
                  ) : (
                    <div className="d-flex justify-content-center gap-3">
                      {/* Days */}
                      <div className="text-center">
                        <div
                          style={{
                            fontSize: "32px",
                            fontWeight: "700",
                            color: getCountdownColor(),
                            lineHeight: "1",
                          }}
                        >
                          {String(timeRemaining.days).padStart(2, "0")}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            textTransform: "uppercase",
                            marginTop: "4px",
                          }}
                        >
                          Days
                        </div>
                      </div>

                      <div
                        style={{
                          fontSize: "32px",
                          fontWeight: "700",
                          color: getCountdownColor(),
                          lineHeight: "1",
                        }}
                      >
                        :
                      </div>

                      {/* Hours */}
                      <div className="text-center">
                        <div
                          style={{
                            fontSize: "32px",
                            fontWeight: "700",
                            color: getCountdownColor(),
                            lineHeight: "1",
                          }}
                        >
                          {String(timeRemaining.hours).padStart(2, "0")}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            textTransform: "uppercase",
                            marginTop: "4px",
                          }}
                        >
                          Hours
                        </div>
                      </div>

                      <div
                        style={{
                          fontSize: "32px",
                          fontWeight: "700",
                          color: getCountdownColor(),
                          lineHeight: "1",
                        }}
                      >
                        :
                      </div>

                      {/* Minutes */}
                      <div className="text-center">
                        <div
                          style={{
                            fontSize: "32px",
                            fontWeight: "700",
                            color: getCountdownColor(),
                            lineHeight: "1",
                          }}
                        >
                          {String(timeRemaining.minutes).padStart(2, "0")}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            textTransform: "uppercase",
                            marginTop: "4px",
                          }}
                        >
                          Mins
                        </div>
                      </div>

                      <div
                        style={{
                          fontSize: "32px",
                          fontWeight: "700",
                          color: getCountdownColor(),
                          lineHeight: "1",
                        }}
                      >
                        :
                      </div>

                      {/* Seconds */}
                      <div className="text-center">
                        <div
                          style={{
                            fontSize: "32px",
                            fontWeight: "700",
                            color: getCountdownColor(),
                            lineHeight: "1",
                          }}
                        >
                          {String(timeRemaining.seconds).padStart(2, "0")}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            textTransform: "uppercase",
                            marginTop: "4px",
                          }}
                        >
                          Secs
                        </div>
                      </div>
                    </div>
                  )}

                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      marginTop: "12px",
                    }}
                  >
                    Expires: {new Date(availabilityEndDate).toLocaleString()}
                  </div>
                </div>
              )}

              {/* QR Code Image */}
              <div className="mb-4">
                <img
                  src={qrCode}
                  alt="Survey QR Code"
                  style={{
                    maxWidth: "400px",
                    width: "100%",
                    height: "auto",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    padding: "20px",
                    backgroundColor: "#fff",
                  }}
                />
              </div>

              {/* Survey URL */}
              {surveyUrl && (
                <div className="mb-4">
                  <Label className="mb-2">Survey URL</Label>
                  <InputGroup>
                    <Input
                      type="text"
                      value={surveyUrl}
                      readOnly
                      style={{ backgroundColor: "#f8f9fa" }}
                    />
                    <InputGroupText>
                      <CopyToClipboard text={surveyUrl} onCopy={handleCopyLink}>
                        <Button color="link" className="p-0">
                          <FaCopy />
                        </Button>
                      </CopyToClipboard>
                    </InputGroupText>
                  </InputGroup>
                </div>
              )}

              {/* Action Buttons */}
              <div className="d-flex justify-content-center gap-2">
                <Button color="primary" onClick={handleDownload}>
                  <FaDownload className="me-2" />
                  Download QR Code
                </Button>
                {surveyUrl && (
                  <CopyToClipboard text={surveyUrl} onCopy={handleCopyLink}>
                    <Button color="info">
                      <FaCopy className="me-2" />
                      Copy Link
                    </Button>
                  </CopyToClipboard>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center p-5">
              <p className="text-muted">No QR code available</p>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default SurveyQRCode;